/**
 * Single source of truth for the static page map.
 *
 * Both the HTML generator (scripts/generate-pages.ts) and the Vite multi-page build
 * input list are derived from this table, so a route cannot exist in one and not the
 * other. Every route is emitted as a real `<dir>/index.html` file, which is what makes
 * direct access and reload work without any server rewrite rules.
 */

import { FORMS, type FormKind } from '../print-forms/model';

export const LANGS = ['ko', 'en'] as const;
export type Lang = (typeof LANGS)[number];

export const TOOLS = ['merge', 'extract', 'organize', 'rotate'] as const;
export type Tool = (typeof TOOLS)[number];

export type PageKind =
  | 'langPicker'
  | 'home'
  | 'tool'
  | 'printHub'
  | 'printForm'
  | 'about'
  | 'privacy'
  | 'contact';

export interface Route {
  /** Directory relative to the project root; '' means the root index.html. */
  dir: string;
  /** URL path as served, always ending in '/'. */
  path: string;
  kind: PageKind;
  /** Absent only for the root language picker. */
  lang?: Lang;
  tool?: Tool;
  /** Present only on the printable-form pages. */
  form?: FormKind;
}

/**
 * tools-hub integration: every PDF page lives under /<lang>/pdf/ inside the combined site.
 * The combined site owns / and /<lang>/, so this module emits no root language picker.
 */
export const SECTION = 'pdf';

/** The printable forms live under one hub: /<lang>/pdf/print-forms/<form>/. */
export const PRINT_FORMS_SEGMENT = 'print-forms';

function printRoutes(lang: Lang): Route[] {
  return [
    {
      dir: `${lang}/${SECTION}/${PRINT_FORMS_SEGMENT}`,
      path: `/${lang}/${SECTION}/${PRINT_FORMS_SEGMENT}/`,
      kind: 'printHub',
      lang,
    },
    ...FORMS.map((form) => ({
      dir: `${lang}/${SECTION}/${PRINT_FORMS_SEGMENT}/${form}`,
      path: `/${lang}/${SECTION}/${PRINT_FORMS_SEGMENT}/${form}/`,
      kind: 'printForm' as const,
      lang,
      form,
    })),
  ];
}

function toolRoutes(lang: Lang): Route[] {
  return TOOLS.map((tool) => ({
    dir: `${lang}/${SECTION}/${tool}`,
    path: `/${lang}/${SECTION}/${tool}/`,
    kind: 'tool' as const,
    lang,
    tool,
  }));
}

function infoRoutes(lang: Lang): Route[] {
  return (['about', 'privacy', 'contact'] as const).map((kind) => ({
    dir: `${lang}/${SECTION}/${kind}`,
    path: `/${lang}/${SECTION}/${kind}/`,
    kind,
    lang,
  }));
}

export const ROUTES: Route[] = [
  ...LANGS.flatMap((lang): Route[] => [
    { dir: `${lang}/${SECTION}`, path: `/${lang}/${SECTION}/`, kind: 'home', lang },
    ...toolRoutes(lang),
    ...printRoutes(lang),
    ...infoRoutes(lang),
  ]),
];

/** The same page in the other language — used by the language switch. */
export function otherLangPath(route: Route): string {
  if (!route.lang) return '/';
  const other: Lang = route.lang === 'ko' ? 'en' : 'ko';
  return route.path.replace(`/${route.lang}/`, `/${other}/`);
}

export function routeFor(lang: Lang, kind: PageKind, tool?: Tool): Route {
  const found = ROUTES.find(
    (r) => r.lang === lang && r.kind === kind && r.tool === tool && r.form === undefined,
  );
  if (!found) throw new Error(`No route for ${lang}/${kind}/${tool ?? '-'}`);
  return found;
}

export function printFormRoute(lang: Lang, form: FormKind): Route {
  const found = ROUTES.find((r) => r.lang === lang && r.kind === 'printForm' && r.form === form);
  if (!found) throw new Error(`No print form route for ${lang}/${form}`);
  return found;
}

/** All emitted HTML files, relative to the project root. */
export function htmlInputs(): string[] {
  return ROUTES.map((r) => (r.dir === '' ? 'index.html' : `${r.dir}/index.html`));
}
